/* eslint-disable no-undef */
import * as React from 'react';
import { observer } from 'mobx-react-lite';
import appClient from '../../../common/services/AppClient';
import { endpoints } from '../../../../config';

type Props = {
    packageId: string;
    packageName: string;
    highlightedRow?: number;
    higlightRowCallback?: (higlightRow: number) => void;
    tableOffset?: number;
    style?: React.CSSProperties;
    packageUrl?: string;
    sheetsbar?: boolean;
    onLoadComplete?: () => void;
};

const DocumentExcel: React.FC<Props> = ({
    packageId,
    packageName,
    highlightedRow,
    higlightRowCallback,
    tableOffset = 0,
    style = { width: '100%', height: 'calc(50vh - 80px)' },
    packageUrl,
    sheetsbar = false,
    onLoadComplete
}) => {
    const [spreadsheet, setSpreadsheet] = React.useState<kendo.ui.Spreadsheet | undefined>(undefined);

    const [blob, setBlob] = React.useState<Blob | null>(null);

    const ref = React.useRef<HTMLDivElement>(null);

    const onSelect = React.useCallback(
        (event: kendo.ui.SpreadsheetSelectEvent) => {
            if (!event.range) {
                return;
            }

            const range = event.range as kendo.spreadsheet.Range & {
                topLeft: () => { col: number; rel: number; row: number };
            };

            const cellRef = range.topLeft();
            const higlightRow = cellRef.row - tableOffset;

            if (higlightRowCallback) {
                higlightRowCallback(higlightRow);
            }
        },
        [tableOffset, higlightRowCallback]
    );

    React.useEffect(() => {
        const url = packageUrl ?? endpoints.manageUrl + `document/preview/${packageId}`;

        setBlob(null);

        appClient.get<Blob>(url, 'blob').then(resp => {
            if (resp.isOk()) {
                resp.map(setBlob);
            } else {
                console.error(`Can't load file ${packageId}:${packageName} for preview`);
            }
        });
    }, [packageId, packageName, packageUrl]);

    React.useEffect(() => {
        if (!ref.current) {
            return;
        }

        const kendoSpreadsheet = $(ref.current)
            .kendoSpreadsheet({
                sheetsbar: sheetsbar,
                toolbar: false,
                select: onSelect
            })
            .data('kendoSpreadsheet');

        setSpreadsheet(kendoSpreadsheet);
    }, [onSelect, sheetsbar]);

    React.useEffect(() => {
        if (!spreadsheet || !blob) {
            return;
        }

        spreadsheet.fromFile(blob);

        // Disable all cells
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        spreadsheet.one('render', function (e: any) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            e.sender.sheets().forEach(function (sheet: any) {
                // eslint-disable-next-line
                sheet.range(0, 0, sheet._rows._count, sheet._columns._count).enable(false);
            });

            if (onLoadComplete) {
                onLoadComplete();
            }
        });
    }, [spreadsheet, blob, onLoadComplete]);

    React.useEffect(() => {
        if (!spreadsheet || typeof highlightedRow !== 'number') {
            return;
        }

        const sheet = spreadsheet.activeSheet();

        sheet.range(`A${highlightedRow + 1}:AA${highlightedRow + 1}`).select();
    }, [spreadsheet, highlightedRow]);

    return <div ref={ref} style={style} />;
};

export default observer(DocumentExcel);
